/**
 * Billing client (tiers T10) — thin wrappers over the T9 Lambdas. Both
 * calls return a Stripe-hosted URL the caller redirects to; the page never
 * touches card data or the Stripe key.
 */
import { asyncApiConfig } from '../config/async-api-config.ts';
import { authHeader, requestJson, safeText } from './apiClient.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

/** The six catalog plans (Stripe price lookup keys). */
export type PlanLookupKey =
  | 'premium_monthly'
  | 'premium_annual'
  | 'plus_monthly'
  | 'plus_annual'
  | 'pro_monthly'
  | 'pro_annual';

/** Start a subscription checkout; returns the hosted Checkout URL. */
export async function startCheckout(lookupKey: PlanLookupKey): Promise<string> {
  const { url } = await requestJson<{ url: string }>(`${asyncApiConfig.baseUrl}/billing/checkout`, {
    method: 'POST',
    body: { lookupKey },
    logTag: 'billingApi',
    friendlyError: 'Checkout could not be started. Please try again.',
  });
  return url;
}

/**
 * Open the Stripe customer portal; `null` when the user has no billing
 * account yet (the page routes them to checkout instead).
 */
export async function openPortal(): Promise<string | null> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/billing/portal`, {
    method: 'POST',
    headers: await authHeader(),
    signal: requestTimeout(),
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    console.error(`billingApi portal failed (${response.status})`, await safeText(response));
    throw new Error('The billing portal could not be opened. Please try again.');
  }
  const body = (await response.json()) as { url?: string };
  return body.url ?? null;
}

export interface SubscriptionDetails {
  readonly status: string;
  readonly lookupKey: string | null;
  readonly interval: string | null;
  readonly amount: number | null;
  readonly currency: string;
  // Null when Stripe doesn't expose a current period end for this subscription
  // (state without a renewal, or a value the server couldn't parse).
  readonly renewsAt: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly card: { readonly brand: string; readonly last4: string } | null;
}

/** Current subscription details from Stripe; null when none is active. */
export async function getSubscription(): Promise<SubscriptionDetails | null> {
  const { subscription } = await requestJson<{ subscription: SubscriptionDetails | null }>(
    `${asyncApiConfig.baseUrl}/billing/subscription`,
    { logTag: 'billingApi', friendlyError: 'Subscription details are unavailable.' },
  );
  return subscription;
}

/** The user's paid tier from their Cognito groups (highest wins). */
export function tierOfGroups(
  groups: readonly string[],
): 'vip' | 'pro' | 'plus' | 'premium' | null {
  for (const tier of ['vip', 'pro', 'plus', 'premium'] as const) {
    if (groups.includes(tier)) {
      return tier;
    }
  }
  return null;
}
